import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  gettingUserProfile,
  updateUserProfile,
  sendPhoneChangeOtp,
  verifyPhoneChangeOtp,
  getWhatIsMyBusiness,
} from '../../Redux/Actions/UserAction';
import Sidebar from '../common/Sidebar/Sidebar';
import { useTranslation } from 'react-i18next';
import { getSettingsSidebarOptions } from './sidebarOptions';

// Hide the Bootstrap #edit-modal completely. Its focus-trap (enforceFocus)
// otherwise steals focus from any input we render on top of it — which is why
// the earlier SweetAlert OTP box "couldn't be typed in". Same teardown the
// updateUserProfile success path uses.
const hideEditModal = () => {
  const el = document.getElementById('edit-modal');
  if (!el) return;
  try {
    // eslint-disable-next-line global-require
    const { Modal } = require('bootstrap');
    (Modal.getInstance(el) || Modal.getOrCreateInstance(el)).hide();
  } catch (e) {}
  try {
    if (window.$ && window.$(el).modal) window.$(el).modal('hide');
  } catch (e) {}
  el.classList.remove('show');
  el.style.display = 'none';
  document.body.classList.remove('modal-open');
  document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
};

export default function SettingsEditProfile() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    oldPassword: '',
    password: '',
    rePassword: '',
  });

  const [error, setError] = useState({});
  const [showPwd, setShowPwd] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  // --- Phone-change OTP (registration-style, in-component so it can stay open
  // on a wrong code and offer Resend after a 60s cooldown). ---
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpBoxes, setOtpBoxes] = useState(new Array(4).fill(''));
  const [resendIn, setResendIn] = useState(60);
  const [otpError, setOtpError] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  // The verified-number context + the profile payload to save once verified.
  const [otpCtx, setOtpCtx] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const otpWrapRef = useRef(null);

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(gettingUserProfile());
    dispatch(getWhatIsMyBusiness());
  }, []);

  const userProfileData = useSelector((state) => state.home.userProfileData);
  const getMybusiness = useSelector((state) => state.home?.getMybusiness);

  console.log(userProfileData);

  // Resend cooldown ticker (only while the OTP modal is open).
  useEffect(() => {
    if (!otpOpen || resendIn <= 0) return undefined;
    const id = setInterval(() => {
      setResendIn((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [otpOpen, resendIn]);

  // TESTING ONLY (SMS gateway down): /otp/resend echoes the fresh code back, so
  // auto-fill the 4 boxes exactly like the registration screen. The user just
  // taps Verify. REMOVE this fill once SMS delivery is restored.
  const autofillFromResponse = (res) => {
    const real = String(res?.data?.response?.otp || '').replace(/\D/g, '');
    if (/^\d{4}$/.test(real)) setOtpBoxes(real.split(''));
  };

  // Send (or resend) the OTP to the NEW number and (re)start the cooldown.
  const sendOtp = (ctx) => {
    setOtpBusy(true);
    setOtpError('');
    return dispatch(sendPhoneChangeOtp(ctx))
      .then((res) => {
        setOtpBusy(false);
        setResendIn(60);
        autofillFromResponse(res);
      })
      .catch((err) => {
        setOtpBusy(false);
        setOtpError(
          err?.response?.data?.response?.message ||
            t('Could not send OTP. Please try again.')
        );
      });
  };

  const openOtpModal = (data, ctx) => {
    // Hide the Bootstrap edit modal first so its focus-trap can't steal focus.
    hideEditModal();
    setPendingData(data);
    setOtpCtx(ctx);
    setOtpBoxes(new Array(4).fill(''));
    setOtpError('');
    setResendIn(60);
    setOtpOpen(true);
    sendOtp(ctx);
  };

  const closeOtpModal = () => {
    setOtpOpen(false);
    setOtpBoxes(new Array(4).fill(''));
    setOtpError('');
    setPendingData(null);
    setOtpCtx(null);
  };

  const focusBox = (index) => {
    const inputs = otpWrapRef.current?.querySelectorAll('input');
    if (inputs && inputs[index]) inputs[index].focus();
  };

  const onOtpChange = (value, index) => {
    const v = (value || '').replace(/\D/g, '').slice(0, 1);
    setOtpBoxes((prev) => prev.map((d, i) => (i === index ? v : d)));
    setOtpError('');
    if (v !== '' && index < 3) focusBox(index + 1);
  };

  const onOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otpBoxes[index] !== '') {
        setOtpBoxes((prev) => prev.map((d, i) => (i === index ? '' : d)));
      } else if (index > 0) {
        focusBox(index - 1);
        setOtpBoxes((prev) => prev.map((d, i) => (i === index - 1 ? '' : d)));
      }
    }
  };

  const onOtpPaste = (e) => {
    e.preventDefault();
    const digits = (e.clipboardData.getData('text') || '')
      .replace(/\D/g, '')
      .slice(0, 4);
    if (!digits) return;
    const next = new Array(4).fill('');
    for (let i = 0; i < digits.length; i += 1) next[i] = digits[i];
    setOtpBoxes(next);
    focusBox(Math.min(digits.length, 3));
  };

  const verifyOtp = () => {
    const otp = otpBoxes.join('');
    if (!/^\d{4}$/.test(otp)) {
      setOtpError(t('Please enter the 4 digit OTP'));
      return;
    }
    setOtpBusy(true);
    setOtpError('');
    dispatch(verifyPhoneChangeOtp({ ...otpCtx, otp }))
      .then(() => {
        setOtpBusy(false);
        const data = pendingData;
        closeOtpModal();
        dispatch(updateUserProfile(data));
      })
      .catch(() => {
        // Wrong / expired code — KEEP the modal open so the user can retry.
        setOtpBusy(false);
        setOtpBoxes(new Array(4).fill(''));
        setOtpError(t('Incorrect OTP. Please try again.'));
        focusBox(0);
      });
  };

  const resendOtp = () => {
    if (resendIn > 0 || otpBusy) return;
    setOtpBoxes(new Array(4).fill(''));
    sendOtp(otpCtx);
  };

  useEffect(() => {
    setProfile({
      firstName: userProfileData?.firstName,
      lastName: userProfileData?.lastName,
      mobile: userProfileData?.mobileNumber,
      email: userProfileData?.email,
      oldPassword: '',
      password: '',
      rePassword: '',
    });
  }, [userProfileData]);

  const onSubmitHandle = () => {
    let err = {};
    if (!profile.firstName) {
      err.firstName = 'Username Required';
    }
    // [FB2-#2d] Email removed from Edit Profile (not collected at registration).
    if (!profile.mobile) {
      err.mobileNumber = 'Mobile No Required';
    }
    // [FB2-#2c] A password change is driven by entering a NEW password (or its
    // confirmation) — NOT by the current-password field alone. Chrome may
    // autofill the saved password into the current-password box; that must not
    // force a "new password required" error on a phone-only update.
    const changingPassword = !!(profile.password || profile.rePassword);
    if (changingPassword) {
      if (!profile.oldPassword) {
        err.oldPassword = 'Current password is required';
      }
      if (!profile.password) {
        err.password = 'New password is required';
      } else if (profile.password.length < 8) {
        // [FB2-#2b] Enforce the SAME strength rules as the Registration form:
        // min 8 chars + one lowercase, one uppercase, one digit, one special.
        err.password = t('Password must have at least 8 characters');
      } else if (
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(
          profile.password
        )
      ) {
        err.password = t(
          'Password must include uppercase, lowercase, number, and special character'
        );
      }
      if (profile.password !== profile.rePassword) {
        err.rePassword = 'Both passwords should be the same';
      }
    }
    setError(err);
    if (Object.keys(err).length > 0) {
      return false;
    }
    const data = {
      firstName: profile.firstName,
      lastName: profile.lastName,
      // The users table column is `mobileNumber` — sending `mobile` was silently
      // dropped by Sequelize (User.update ignores unknown attrs), so the number
      // never actually changed. Send the real column name.
      mobileNumber: profile.mobile,
    };
    if (changingPassword) {
      data.oldPassword = profile.oldPassword;
      data.password = profile.password;
      data.rePassword = profile.rePassword;
    }
    // If the phone number changed, verify the NEW number via OTP before saving.
    const phoneChanged =
      profile.mobile &&
      userProfileData?.mobileNumber &&
      String(profile.mobile) !== String(userProfileData.mobileNumber);
    if (phoneChanged) {
      openOtpModal(data, {
        userId: userProfileData?.userId,
        countryCode: userProfileData?.countryCode || '+965',
        mobileNumber: profile.mobile,
      });
      return;
    }
    dispatch(updateUserProfile(data));
  };
  console.log('error', error);

  const sideBarOpt = getSettingsSidebarOptions(t, getMybusiness);

  return (
    <div>
      <div class="main-content-area">
        <div class="container">
          <h2 class="mb-4">{t('Settings')} </h2>

          <div class="row justify-content-between">
            <Sidebar options={sideBarOpt} showPackage={false} />

            <div class="col-12 col-sm-12 col-md-9 col-lg-9">
              <h5 class="mb-4">{t('Edit Profile')}</h5>

              <table class="table table-bordered">
                <tbody>
                  <tr>
                    <td>{t('Username')}</td>
                    <td>{userProfileData?.firstName}</td>
                  </tr>
                  <tr>
                    <td>{t('Mobile Number')}</td>
                    <td>{userProfileData?.mobileNumber}</td>
                  </tr>
                  {/* <tr>
										<td>Email ID</td>
										<td>{ userProfileData?.email }</td>
									</tr> */}
                  <tr>
                    <td>{t('Password')}</td>
                    <td>**********</td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <a
                        class="btn btn-dark btn-block"
                        href="javascript:void(0);"
                        role="button"
                        data-toggle="modal"
                        data-target="#edit-modal"
                      >
                        {t('Edit')}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* <!-- EDIT --> */}
          <div
            class="modal fade"
            id="edit-modal"
            tabindex="-1"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="exampleModalLabel">
                    {t('Edit Profile')}
                  </h5>
                  <button
                    type="button"
                    class="close"
                    data-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <form>
                    <div class="form-group">
                      <label for="reg-fn" class="sr-only">
                        {t('Username')}
                      </label>
                      <input
                        onInput={() => setError({ ...error, firstName: '' })}
                        type="text"
                        class="form-control"
                        name="firstName"
                        value={profile.firstName}
                        onChange={onChangeHandler}
                        id="reg-fn"
                        placeholder={t('Enter your Username')}
                      />
                      {error?.firstName && (
                        <span className="form-error">{error?.firstName}</span>
                      )}
                    </div>

                    <div class="form-group">
                      <label for="reg-mobile" class="sr-only">
                        {t('Mobile Number')}
                      </label>
                      <input
                        onInput={() => setError({ ...error, mobile: '' })}
                        type="tel"
                        class="form-control"
                        name="mobile"
                        value={profile.mobile}
                        onChange={onChangeHandler}
                        id="reg-mobile"
                        placeholder={t('Enter your Mobile Number')}
                      />
                      {error?.mobileNumber && (
                        <span className="form-error">
                          {error?.mobileNumber}
                        </span>
                      )}
                    </div>

                    {/* [FB2-#2d] Email field removed — registration never asks
                        for an email (username + phone + password only), so it
                        doesn't belong on Edit Profile either. */}

                    <p
                      style={{
                        margin: '4px 0 8px',
                        fontSize: '13px',
                        color: '#888',
                      }}
                    >
                      {t(
                        'Leave the password fields empty if you only want to update your details.'
                      )}
                    </p>

                    <div class="form-group" style={{ position: 'relative' }}>
                      <label for="reg-old-password" class="sr-only">
                        {t('Current Password')}
                      </label>
                      <input
                        onInput={() => setError({ ...error, oldPassword: '' })}
                        type={showPwd.old ? 'text' : 'password'}
                        autoComplete="new-password"
                        class="form-control"
                        name="oldPassword"
                        value={profile.oldPassword}
                        onChange={onChangeHandler}
                        id="reg-old-password"
                        placeholder={t('Current Password')}
                        style={{ paddingRight: '42px' }}
                      />
                      <span
                        onClick={() =>
                          setShowPwd({ ...showPwd, old: !showPwd.old })
                        }
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '12px',
                          cursor: 'pointer',
                          color: '#888',
                        }}
                      >
                        <i
                          className={
                            showPwd.old ? 'fa fa-eye-slash' : 'fa fa-eye'
                          }
                        ></i>
                      </span>
                      {error?.oldPassword && (
                        <span className="form-error">{error?.oldPassword}</span>
                      )}
                    </div>

                    <div class="form-group" style={{ position: 'relative' }}>
                      <label for="reg-password" class="sr-only">
                        {t('New Password')}
                      </label>
                      <input
                        onInput={() => setError({ ...error, password: '' })}
                        type={showPwd.new ? 'text' : 'password'}
                        autoComplete="new-password"
                        class="form-control"
                        name="password"
                        value={profile.password}
                        onChange={onChangeHandler}
                        id="reg-password"
                        placeholder={t('New Password')}
                        style={{ paddingRight: '42px' }}
                      />
                      <span
                        onClick={() =>
                          setShowPwd({ ...showPwd, new: !showPwd.new })
                        }
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '12px',
                          cursor: 'pointer',
                          color: '#888',
                        }}
                      >
                        <i
                          className={
                            showPwd.new ? 'fa fa-eye-slash' : 'fa fa-eye'
                          }
                        ></i>
                      </span>
                      {error?.password && (
                        <span className="form-error">{error?.password}</span>
                      )}
                    </div>

                    <div class="form-group" style={{ position: 'relative' }}>
                      <label for="reg-con-password" class="sr-only">
                        {t('Confirm Password')}
                      </label>
                      <input
                        onInput={() => setError({ ...error, rePassword: '' })}
                        type={showPwd.confirm ? 'text' : 'password'}
                        autoComplete="new-password"
                        class="form-control"
                        name="rePassword"
                        value={profile.rePassword}
                        onChange={onChangeHandler}
                        id="reg-con-password"
                        placeholder={t('Confirm Password')}
                        style={{ paddingRight: '42px' }}
                      ></input>
                      <span
                        onClick={() =>
                          setShowPwd({ ...showPwd, confirm: !showPwd.confirm })
                        }
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '12px',
                          cursor: 'pointer',
                          color: '#888',
                        }}
                      >
                        <i
                          className={
                            showPwd.confirm ? 'fa fa-eye-slash' : 'fa fa-eye'
                          }
                        ></i>
                      </span>
                      {error?.rePassword && (
                        <span className="form-error">{error?.rePassword}</span>
                      )}
                    </div>
                  </form>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-warning"
                    onClick={onSubmitHandle}
                  >
                    {t('Save Details')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* <!-- EDIT --> */}

          {/* <!-- PHONE-CHANGE OTP (registration-style; stays open on error) --> */}
          {otpOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 20000,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
              }}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: '14px',
                  width: '100%',
                  maxWidth: '420px',
                  padding: '28px 22px',
                  textAlign: 'center',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                }}
              >
                <img
                  src="/assets/images/otp-icon.png"
                  alt=""
                  className="img-fluid"
                  style={{ maxHeight: '72px', marginBottom: '8px' }}
                />
                <h4 className="mb-2">{t('Verify your new number')}</h4>
                <p className="mb-1" style={{ color: '#555' }}>
                  {t('Please enter the 4 digit OTP sent to your phone')}
                </p>
                <p className="mb-3" style={{ fontWeight: 600 }}>
                  {otpCtx?.countryCode} {otpCtx?.mobileNumber}
                </p>

                <div
                  ref={otpWrapRef}
                  className="row text-center"
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 0 6px',
                  }}
                >
                  {otpBoxes.map((data, index) => (
                    <div className="col-3" key={index}>
                      <input
                        className="form-control m-2 otp-input"
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={data}
                        style={{ textAlign: 'center' }}
                        onChange={(e) => onOtpChange(e.target.value, index)}
                        onKeyDown={(e) => onOtpKeyDown(e, index)}
                        onPaste={onOtpPaste}
                        onFocus={(e) => e.target.select()}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={index === 0}
                      />
                    </div>
                  ))}
                </div>

                {otpError && (
                  <p className="mb-2" style={{ color: '#e03131', fontSize: '14px' }}>
                    {otpError}
                  </p>
                )}

                {resendIn > 0 ? (
                  <span
                    className="m-1 btn btn-link"
                    style={{
                      color: '#9aa0a6',
                      cursor: 'not-allowed',
                      textDecoration: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    {t('Resend OTP in')} 0:{String(resendIn).padStart(2, '0')}
                  </span>
                ) : (
                  <a
                    href="#"
                    className="m-1 btn btn-link"
                    onClick={(e) => {
                      e.preventDefault();
                      resendOtp();
                    }}
                  >
                    {t('Resend Otp?')}
                  </a>
                )}

                <button
                  type="button"
                  className="btn btn-warning btn-block m-1"
                  disabled={otpBusy}
                  onClick={verifyOtp}
                >
                  {otpBusy ? t('Please wait...') : t('Verify')}
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-block m-1"
                  style={{ color: '#888' }}
                  onClick={closeOtpModal}
                >
                  {t('Cancel')}
                </button>
              </div>
            </div>
          )}
          {/* <!-- PHONE-CHANGE OTP --> */}
        </div>
      </div>
    </div>
  );
}
